import { viewCoverImage } from './viewCoverImage.js';

export function isExhibitionSvgSource(source = '') {
  if (/^data:image\/svg\+xml[;,]/i.test(source)) return true;
  try { return /\.svg$/i.test(new URL(source, 'https://www.henryw.me').pathname); }
  catch { return false; }
}

// The image determines the mount. The border is a physical inset on every side.
export function exhibitionImageSize(sourceWidth, sourceHeight, maxWidth, maxHeight, border = .03) {
  const ratio = sourceWidth > 0 && sourceHeight > 0 ? sourceWidth / sourceHeight : maxWidth / maxHeight;
  const width = Math.min(maxWidth, maxHeight * ratio);
  const height = width / ratio;
  return { width, height, outerWidth: width + border * 2, outerHeight: height + border * 2 };
}

// Both the architect and renderer use the complete group width. Decoding an
// image can change its aspect ratio, but never its reserved span on the wall.
export function exhibitionPictureGroup(media = []) {
  const unique = [...new Map(media.map(image => [image.alt || image.src, image])).values()];
  const pictures = unique.length > 3 ? [unique[0], unique[Math.floor(unique.length / 2)], unique.at(-1)] : unique;
  const width = pictures.length > 1 ? 1.82 : 3.6;
  const height = pictures.length > 1 ? 2.55 : 3.1;
  return { pictures, width, height, spacing: 2, span: pictures.length ? (pictures.length - 1) * 2 + width + .06 : 3.7 };
}

const decodedImages=new Map(),displayImages=new Map(),imageQueue=[];
const warmingImages=new Map();
const warmingQueue=[];
let activeWarmers=0;
export function warmExhibitionImages(sources,{maxDimension=384}={}) {
  const work=[...new Set(sources.filter(Boolean))].map(source=>{
    const key=`${source}|${maxDimension}`;
    if(warmingImages.has(key))return warmingImages.get(key);
    const result=new Promise(resolve=>{warmingQueue.push({source,maxDimension,resolve});});
    warmingImages.set(key,result);return result;
  });
  function next(){
    while(activeWarmers<2&&warmingQueue.length){
      const job=warmingQueue.shift();activeWarmers++;
      preloadExhibitionImage(job.source,{maxDimension:job.maxDimension,priority:'low'}).catch(()=>{
        warmingImages.delete(`${job.source}|${job.maxDimension}`);
      }).finally(()=>{warmingImages.delete(`${job.source}|${job.maxDimension}`);activeWarmers--;job.resolve();next();});
    }
  }
  next();return Promise.all(work);
}
let activeImages=0;
function nextImage(){
  while(activeImages<4&&imageQueue.length){
    const job=imageQueue.shift();activeImages++;
    job().finally(()=>{activeImages--;nextImage();});
  }
}

// Keep the decoded HTML image alive as well as its ImageBitmap. The Hall uses
// the bitmap for Three.js, while the collection preview uses the HTML image.
// Retaining both prevents the preview panel from decoding the same illustration
// again when it first appears.
export function preloadExhibitionImage(source,{maxDimension=1024,priority='auto'}={}){
  const resolved=viewCoverImage(source,maxDimension);
  const url=new URL(resolved,window.location.href).href;
  if(displayImages.has(url))return displayImages.get(url);
  const result=(async()=>{
    const image=new Image();image.crossOrigin='anonymous';image.decoding='async';image.fetchPriority=priority;image.src=url;
    if(typeof image.decode==='function')await image.decode();
    else await new Promise((resolve,reject)=>{image.onload=resolve;image.onerror=reject;});
    return image;
  })().catch(error=>{displayImages.delete(url);if(resolved!==source)return preloadExhibitionImage(new URL(source,window.location.href).href,{maxDimension:0,priority});throw error;});
  displayImages.set(url,result);
  if(displayImages.size>40)displayImages.delete(displayImages.keys().next().value);
  return result;
}

// Share decoded pixels across the hall, its preview and an individual visit.
// GPU textures still belong to their renderer and are disposed with the scene.
export function loadExhibitionImage(source,{maxDimension=1024}={}){
  const url=new URL(viewCoverImage(source,maxDimension),window.location.href).href;
  const key=`${url}|${maxDimension}`;
  if(decodedImages.has(key))return decodedImages.get(key);
  const result=new Promise((resolve,reject)=>{
    imageQueue.push(async()=>{
      try{
        let image=[...document.images].find(item=>item.complete&&item.naturalWidth&&(item.currentSrc||item.src)===url&&(item.crossOrigin==='anonymous'||new URL(url).origin===window.location.origin));
        if(!image)image=await preloadExhibitionImage(source,{maxDimension});
        const svg=isExhibitionSvgSource(image.currentSrc||image.src||url);
        const ratio=maxDimension>0?maxDimension/Math.max(image.naturalWidth,image.naturalHeight):1;
        const scale=svg?ratio:Math.min(1,ratio);
        const width=Math.max(1,Math.round(image.naturalWidth*scale)),height=Math.max(1,Math.round(image.naturalHeight*scale));
        let pixels=image;
        if(svg){
          // An SVG with only a viewBox can decode successfully yet produce a
          // transparent ImageBitmap or fail a direct WebGL upload. Canvas2D
          // renders the original vector at the texture's requested resolution.
          pixels=document.createElement('canvas');pixels.width=width;pixels.height=height;
          pixels.getContext('2d').drawImage(image,0,0,width,height);
        }
        if(typeof createImageBitmap==='function'){
          try{pixels=await createImageBitmap(pixels,{imageOrientation:'flipY',premultiplyAlpha:'none',resizeWidth:width,resizeHeight:height,resizeQuality:'high'});}catch{ /* The decoded image or rasterised SVG remains a valid texture source. */ }
        }
        resolve(pixels);
      }catch(error){decodedImages.delete(key);reject(error);}
    });
  });
  decodedImages.set(key,result);
  if(decodedImages.size>16)decodedImages.delete(decodedImages.keys().next().value);
  nextImage();return result;
}
